import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Wallet, BarChart3, Users, Plug, AlertOctagon, Gauge, TrendingUp } from 'lucide-react';
import { adminDashboard, adminReports, adminRealAccount } from '@/api/admin';
import type { EquityRange } from '@/api/types';
import { useSystemStore } from '@/stores/system';
import { KpiCard } from '@/components/widgets/KpiCard';
import { ProgressBar } from '@/components/widgets/ProgressBar';
import { Pill } from '@/components/widgets/Pill';
import { Sparkline } from '@/components/widgets/Sparkline';
import { EquityChart } from '@/components/widgets/EquityChart';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ForceLiquidateButton, UnlockTradeButton } from '@/features/force-liquidate';
import { DetachedBadge, normalizeDetachState } from '@/features/detach';
import { fmtMoney, fmtPct, moneyToNumber } from '@/lib/format';
import { ApiError } from '@/lib/http';
import { cn } from '@/lib/utils';
import { PerformanceOverview } from '@/features/performance/PerformancePanel';

// inverted=true 用于「净值水位」(越高越健康):颜色锚定后台可配下限 floor(= 后端急停线),
// 不再写死 70/85。<floor 红(已破/将触发急停)/ floor~floor+15 黄(逼近)/ 其余 绿。
// ceiling 用于「当日亏损」(越高越危险):颜色锚定后台可配上限(= 后端急停线 maxDailyLossPercent),
// 不传 ceiling 时保持旧的固定 80/50 通用占用带(保证金/总持仓/API 错误率不受影响);ceiling 分支下
// floor 形参不生效,调用处按位置传参占位即可。0.7 预警提前量是独立选的经验值(当日亏损阈值通常
// 个位数到 20%,不能照搬 capacity.rs 里"相对 100% 满值"的 50/70/85 分级),不是复用后端分级口径。
const riskTone = (pct: number, inverted = false, floor = 70, ceiling?: number): 'brand' | 'warn' | 'down' => {
  if (inverted) {
    if (floor <= 0) return 'brand';
    if (pct < floor) return 'down';
    if (pct < floor + 15) return 'warn';
    return 'brand';
  }
  if (ceiling != null) {
    if (ceiling <= 0) return 'brand';
    if (pct >= ceiling) return 'down';
    if (pct >= ceiling * 0.7) return 'warn';
    return 'brand';
  }
  if (pct >= 80) return 'down';
  if (pct >= 50) return 'warn';
  return 'brand';
};

export function Dashboard() {
  const dashQ = useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: adminDashboard.get,
    refetchInterval: 5_000,
  });

  const hydrate = useSystemStore((s) => s.hydrateFromDashboard);
  useEffect(() => {
    const d = dashQ.data;
    if (d) {
      hydrate(
        d.sessionState.state,
        d.sessionState.tradeLocked,
        d.sessionState.unlockAt,
        d.sessionState.stateSince,
        d.sessionState.emergencyStop,
      );
    }
  }, [dashQ.data, hydrate]);

  const reportsQ = useQuery({
    queryKey: ['admin', 'reports', 'daily'],
    queryFn: adminReports.daily,
    staleTime: 60_000,
  });

  // v2 §13:真实账户容量(单账户承载力 = 整个平台容量)
  const capQ = useQuery({
    queryKey: ['admin', 'real-account', 'capacity'],
    queryFn: adminRealAccount.capacity,
    refetchInterval: 5_000,
  });
  const cap = capQ.data;

  const data = dashQ.data;
  // 虚实脱钩态。复用已在手的 dashboard 数据,不额外取数;后端两路信号(dashboard.riskLevels.detached
  // 与 capacity.detachedOpenLegs)取并集 —— 灰度中可能只有一路先上线,任一为真即按脱钩态渲染。
  const detach = normalizeDetachState(data?.riskLevels?.detached);
  const detachedLegs = detach.legs || (cap?.detachedOpenLegs ?? 0);
  const detachActive = detach.active || (cap?.detachedOpenLegs ?? 0) > 0;
  const unhedgedPct = data?.riskLevels.unhedgedNotionalPct;
  const floatingPnlNum = data ? moneyToNumber(data.mainAccount.floatingPnl) : 0;
  const realizedNum = data ? moneyToNumber(data.mainAccount.todayRealized) : 0;
  const equityHistory = useMemo(() => (reportsQ.data ?? []).map((r) => r.mainEquity).filter((v): v is number => v != null), [reportsQ.data]);
  const pnlHistory = useMemo(() => (reportsQ.data ?? []).map((r) => r.pnlRealized).filter((v): v is number => v != null), [reportsQ.data]);

  return (
    <div className="p-6 space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-ink-0">仪表板</h1>
          <p className="text-sm text-ink-2 mt-1">主账户 KPI · 当日跟单 · 风控水位 · 雇员状态 · cTID 连接</p>
        </div>
        <div className="flex items-center gap-3">
          <ForceLiquidateButton />
          <UnlockTradeButton />
        </div>
      </header>

      {dashQ.isError && (
        <Card className="border-down/40 bg-down/5">
          <CardContent className="flex items-center gap-3 py-4 text-sm text-down-dim">
            <AlertOctagon className="h-5 w-5" />
            无法加载仪表板:{dashQ.error instanceof ApiError ? dashQ.error.message : '后端不可达,请确认 dev 代理或 backend 服务'}
          </CardContent>
        </Card>
      )}

      {/* 主账户 5 KPI(§8.4.6 + §9.3.1) */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiCard
          label="账户余额"
          tone="neutral"
          icon={<Wallet className="h-4 w-4" />}
          value={fmtMoney(data?.mainAccount.balance)}
          sub={`账户 ${data?.mainAccount.accountLogin ?? '——'}`}
        />
        <KpiCard
          label="实时净值"
          tone={floatingPnlNum >= 0 ? 'up' : 'down'}
          value={fmtMoney(data?.mainAccount.equity)}
          sub={
            equityHistory.length > 0 ? (
              <div className="space-y-1">
                <Sparkline data={equityHistory} width={180} height={28} tone={floatingPnlNum >= 0 ? 'up' : 'down'} />
                <div className="text-[10px] text-ink-3 num">近 30 天净值走势</div>
              </div>
            ) : (
              `浮动 ${fmtMoney(data?.mainAccount.floatingPnl, { sign: true })}`
            )
          }
        />
        <KpiCard
          label="浮动盈亏"
          tone={floatingPnlNum >= 0 ? 'up' : 'down'}
          value={fmtMoney(data?.mainAccount.floatingPnl, { sign: true })}
          sub="实时"
        />
        <KpiCard
          label="保证金占用"
          tone={data && data.mainAccount.marginUsagePct >= 50 ? 'warn' : 'neutral'}
          value={fmtPct(data?.mainAccount.marginUsagePct)}
          sub={fmtMoney(data?.mainAccount.marginUsed)}
        />
        <KpiCard
          label="当日已实现 PnL"
          tone={realizedNum >= 0 ? 'up' : 'down'}
          value={fmtMoney(data?.mainAccount.todayRealized, { sign: true })}
          sub={
            pnlHistory.length > 0 ? (
              <div className="space-y-1">
                <Sparkline data={pnlHistory} width={180} height={28} tone={realizedNum >= 0 ? 'up' : 'down'} />
                <div className="text-[10px] text-ink-3 num">近 30 天日 PnL</div>
              </div>
            ) : undefined
          }
        />
      </section>

      <PerformanceOverview />

      {/* 净值曲线(真实账户;周/月/年/所有 可切换) */}
      <EquityCurveCard />

      {/* v2 §13 真实账户容量概览卡(单账户承载力 = 平台容量) */}
      <section>
        <Card className={cap && capacityLevel(cap.level).border}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Gauge className="h-4 w-4 text-brand" />
              真实账户容量
              {cap && <Pill tone={capacityLevel(cap.level).tone}>{capacityLevel(cap.level).label}</Pill>}
              <DetachedBadge state={detach} />
              <span className="text-xs font-normal text-ink-3">敞口只预警</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* 脱钩态下本卡上半区(真实口径)会掉到接近 0,而用户虚拟持仓仍满仓 —— 不点破会被读成"一切健康" */}
            {detachActive && (
              <div className="rounded-md border border-down/40 bg-down/5 px-3 py-2 text-xs leading-relaxed text-down-dim">
                <span className="font-semibold">虚实已脱钩:本卡的保证金占用率 / 剩余可开 / 毛净敞口全是真实账户口径。</span>
                真实仓被人工平掉后它们会掉到接近 0,但 {detachedLegs || '若干'} 条用户虚拟持仓仍满仓在持、平台正裸承担其全部盈亏,
                <span className="font-semibold">「占用率低」不等于可以继续放量</span>。
                裸敞口规模见下方「已脱钩毛名义」;脱钩态在当晚兜底结算后自然归零。
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 text-sm">
              <Stat label="保证金占用率" tone={cap && cap.marginUsagePct >= 70 ? 'down' : cap && cap.marginUsagePct >= 50 ? 'warn' : 'neutral'} value={cap ? `${cap.marginUsagePct.toFixed(1)}%` : '—'} />
              <Stat label="单边压力等价占用" value={cap ? `${cap.stressMarginUsagePct.toFixed(1)}%` : '—'} />
              <Stat label="毛持仓等价占用" value={cap ? `${cap.grossMarginUsagePct.toFixed(1)}%` : '—'} />
              <Stat label="剩余可开(可用)" value={cap ? fmtMoney({ value: cap.freeMarginCents, digits: 2 }) : '—'} />
              <Stat label="净值" value={cap ? fmtMoney({ value: cap.equityCents, digits: 2 }) : '—'} />
              <Stat label="毛敞口(手)" value={cap ? (cap.grossVolume / 100).toFixed(2) : '—'} />
              <Stat label="净敞口(手)" value={cap ? (cap.netVolume / 100).toFixed(2) : '—'} />
              {/* 「持仓笔数」按真实账户仓位数、「有仓用户数」按虚拟账本腿的用户数 —— 分属两套账,
                  并排成 "x / y" 会被读成同一口径的两个分量,故拆开并各自标注口径。 */}
              <Stat label="真实持仓笔数" value={cap ? `${cap.openPositions}` : '—'} />
              <Stat label="有仓用户数" value={cap ? `${cap.usersWithPositions}` : '—'} />
            </div>
            <div className="text-xs text-ink-3">
              口径:「真实持仓笔数」= 真实账户仓位数(真实账;单一净仓模式下每品种恰好一笔);
              「有仓用户数」= 虚拟账本里仍有在持腿的用户数(虚拟账)。两者分属两套账,不构成比值,脱钩期更不再对应。
            </div>
            {/* 已对冲 / 已脱钩两组毛名义:分级只看前者,后者是平台裸敞口,两者绝不相加算占用率 */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 border-t border-line pt-3 text-sm">
              <Stat label="已对冲毛名义(计入分级)" value={fmtMoney(cap?.hedgedGrossNotional)} />
              <Stat
                label="已脱钩毛名义(平台裸敞口)"
                tone={cap?.detachedGrossNotional && moneyToNumber(cap.detachedGrossNotional) !== 0 ? 'down' : 'neutral'}
                value={fmtMoney(cap?.detachedGrossNotional)}
              />
              <Stat
                label="未对冲名义占比"
                tone={unhedgedPct != null && unhedgedPct > 0 ? 'down' : 'neutral'}
                value={unhedgedPct != null ? `${unhedgedPct.toFixed(1)}%` : '—'}
              />
              <Stat
                label="脱钩持仓 / 涉及用户"
                tone={detachActive ? 'down' : 'neutral'}
                value={cap ? `${cap.detachedOpenLegs ?? 0} / ${cap.detachedUsersCount ?? 0}` : '—'}
              />
            </div>
            <ProgressBar
              label="毛持仓承载预警"
              value={cap?.grossMarginUsagePct ?? 0}
              tone={cap ? riskTone(cap.grossMarginUsagePct) : 'brand'}
              hint={
                detachActive
                  ? '(关注 50% · 预警 70% · 危险 85%;仅系统预警。分级只按已对冲部分算,已脱钩裸敞口不计入)'
                  : '(关注 50% · 预警 70% · 危险 85%；仅系统预警，不限制下单)'
              }
            />
            {/* A1 方向性集中度速览:取同向集中度最高的品种(完整明细见资金账户大屏)。 */}
            {(() => {
              const top = (cap?.exposureBySymbol ?? [])
                .filter((e) => e.grossExposure > 0)
                .sort((a, b) => b.concentrationRatio - a.concentrationRatio)[0];
              if (!top) return null;
              const tone =
                top.concentrationRatio >= 85 ? 'text-down' : top.concentrationRatio >= 70 ? 'text-warn' : 'text-ink-2';
              const dir = top.longTotal >= top.shortTotal ? '同向多' : '同向空';
              return (
                <div className="text-xs text-ink-3">
                  方向性集中(按品种):
                  <span className="text-ink-1 ml-1">{top.symbolName ?? top.symbolId}</span>
                  <span className={cn('ml-1 font-medium', tone)}>
                    {dir} {top.concentrationRatio.toFixed(0)}%
                  </span>
                  <span className="ml-2">· 明细见资金账户大屏</span>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      </section>

      {/* 三联区:当日跟单 · 风控水位 · 雇员摘要 */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-brand" />
              当日跟单流水 · 统计
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="grid grid-cols-3 gap-3">
              <Stat label="信号总数" value={data?.dailySignals.total ?? '—'} />
              <Stat label="已执行" tone="up" value={data?.dailySignals.executed ?? '—'} />
              <Stat label="拦截" tone="warn" value={data?.dailySignals.blocked ?? '—'} />
            </div>
            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-line">
              <Stat
                label="跟单 PnL"
                tone={data && moneyToNumber(data.dailySignals.pnl) >= 0 ? 'up' : 'down'}
                value={fmtMoney(data?.dailySignals.pnl, { sign: true })}
              />
              <Stat label="胜率" value={fmtPct(data?.dailySignals.winRatePct)} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>风控水位</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <ProgressBar
              label="保证金占用"
              value={data?.riskLevels.marginUsagePct ?? 0}
              tone={data ? riskTone(data.riskLevels.marginUsagePct) : 'brand'}
            />
            <ProgressBar
              label="总持仓占用"
              value={data?.riskLevels.totalPositionPct ?? 0}
              tone={data ? riskTone(data.riskLevels.totalPositionPct) : 'brand'}
              hint={detachActive ? '(真实口径,不含已脱钩持仓)' : undefined}
            />
            {/* 脱钩期真实口径归零而虚拟持仓一手没少,虚拟口径是唯一还能反映"平台实际背着多少"的量 */}
            {data?.riskLevels.virtualTotalPositionPct != null && (
              <ProgressBar
                label="总持仓占用 · 虚拟口径"
                value={data.riskLevels.virtualTotalPositionPct}
                tone={riskTone(data.riskLevels.virtualTotalPositionPct)}
                hint="(全体用户虚拟持仓,含已脱钩;与上一条同分母)"
              />
            )}
            <ProgressBar
              label="当日亏损"
              value={data?.riskLevels.dailyLossPct ?? 0}
              tone={data ? riskTone(data.riskLevels.dailyLossPct, false, 70, data.riskLevels.maxDailyLossPercent) : 'brand'}
              hint={
                data && data.riskLevels.maxDailyLossPercent <= 0
                  ? '(未设置上限)'
                  : `(>${data?.riskLevels.maxDailyLossPercent ?? 5}% 急停)`
              }
            />
            <ProgressBar
              label="净值水位"
              value={data?.riskLevels.equityPct ?? 100}
              tone={data ? riskTone(data.riskLevels.equityPct, true, data.riskLevels.equityFloorPercent) : 'up'}
              hint={
                data && data.riskLevels.equityFloorPercent <= 0
                  ? '(未设置下限)'
                  : `(>${data?.riskLevels.equityFloorPercent ?? 70}% 健康)`
              }
            />
            <ProgressBar
              label="API 错误率"
              value={data?.riskLevels.apiErrorRatePct ?? 0}
              tone={data ? riskTone(data.riskLevels.apiErrorRatePct) : 'brand'}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-4 w-4 text-brand" />
              雇员状态摘要
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="grid grid-cols-3 gap-3">
              <Stat label="注册总数" value={data?.employeeSummary.total ?? '—'} />
              <Stat label="在线" tone="up" value={data?.employeeSummary.online ?? '—'} />
              <Stat label="有持仓" value={data?.employeeSummary.withOpenPositions ?? '—'} />
            </div>
            <div className="pt-2 border-t border-line">
              <div className="text-xs text-ink-2 mb-1.5">跟单模式分布</div>
              <div className="flex flex-wrap gap-2">
                <Pill tone="brand">REVERSE {data?.employeeSummary.byMode.REVERSE ?? 0}</Pill>
                <Pill tone="up">FOLLOW {data?.employeeSummary.byMode.FOLLOW ?? 0}</Pill>
              </div>
            </div>
            <div className="pt-2 border-t border-line">
              <div className="text-xs text-ink-2">
                总持仓笔数 <span className="num font-semibold text-ink-0">{data?.employeeSummary.totalOpenPositions ?? 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* cTID 连接 */}
      <section>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plug className="h-4 w-4 text-brand" />
              cTID 连接状态
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {(data?.ctidConnections ?? []).length === 0 && (
              <div className="text-sm text-ink-2 col-span-full">暂无 cTID 数据</div>
            )}
            {data?.ctidConnections.map((ctid) => {
              const isOk = ctid.demo?.state === 'Operational' || ctid.live?.state === 'Operational';
              return (
                <div
                  key={ctid.ctidId}
                  className="rounded-lg border border-line bg-bg-3 p-3 flex flex-col gap-1.5"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium truncate">{ctid.email}</div>
                    <Pill tone={isOk ? 'up' : 'warn'}>{isOk ? '已连接' : '异常'}</Pill>
                  </div>
                  <div className="flex flex-wrap gap-1.5 text-xs text-ink-2">
                    {ctid.demo && <span>demo: {ctid.demo.state}</span>}
                    {ctid.live && <span>live: {ctid.live.state}</span>}
                  </div>
                  <div className="text-xs text-ink-3 num">
                    Token 过期: {ctid.tokenExpiresAt ? new Date(ctid.tokenExpiresAt).toLocaleDateString('zh-CN') : '—'}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </section>
    </div>
  );
}

/** v2 §13.2 容量分级 → 文案 / 灯色 / 卡片边框。 */
function capacityLevel(level: string): { label: string; tone: 'up' | 'warn' | 'down' | 'neutral'; border: string } {
  switch (level) {
    case 'attention':
      return { label: '关注', tone: 'warn', border: 'border-warn/40' };
    case 'warning':
      return { label: '预警', tone: 'down', border: 'border-down/40' };
    case 'danger':
      return { label: '危险', tone: 'down', border: 'border-down/60 bg-down/5' };
    default:
      return { label: '正常', tone: 'up', border: '' };
  }
}

const RANGE_TABS: { label: string; value: EquityRange }[] = [
  { label: '1W', value: '1W' },
  { label: '1M', value: '1M' },
  { label: '1Y', value: '1Y' },
  { label: '所有', value: 'ALL' },
];

/** 抽样点仅用于净值绘图；正式收益由共享收益接口提供，避免入金被算为收益。 */
function EquityCurveCard() {
  const [range, setRange] = useState<EquityRange>('1M');
  const q = useQuery({
    queryKey: ['admin', 'real-account', 'equity-history', range],
    queryFn: () => adminRealAccount.equityHistory(range),
    refetchInterval: 30_000,
    placeholderData: (prev) => prev,
  });

  const points = useMemo(
    () => (q.data?.points ?? []).map((p) => ({ t: p.t, equity: moneyToNumber(p.equity) })),
    [q.data],
  );

  const last = points[points.length - 1]?.equity ?? 0;
  const tone = 'neutral' as const;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center justify-between gap-3">
          <span className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-brand" />
            真实账户净值曲线
          </span>
          <div className="flex items-center gap-1">
            {RANGE_TABS.map((t) => (
              <Button
                key={t.value}
                variant={range === t.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setRange(t.value)}
              >
                {t.label}
              </Button>
            ))}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="mb-2 flex items-baseline gap-3">
          <span className="text-3xl font-bold num text-ink-0">
            {points.length > 0 ? `$${last.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
          </span>
          <span className="text-xs text-ink-3">含出入金的账户净值；收益金额和收益率见上方。</span>
        </div>
        {q.isError ? (
          <div className="flex items-center justify-center text-sm text-down-dim" style={{ height: 220 }}>
            净值曲线加载失败
          </div>
        ) : q.isLoading && points.length === 0 ? (
          <div className="flex items-center justify-center text-sm text-ink-3" style={{ height: 220 }}>
            加载中…
          </div>
        ) : points.length < 2 ? (
          <div className="flex items-center justify-center text-sm text-ink-3" style={{ height: 220 }}>
            该区间暂无足够净值快照(真实账户产生快照后自动出现)
          </div>
        ) : (
          <EquityChart points={points} tone={tone} height={220} />
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, tone = 'neutral' }: { label: string; value: React.ReactNode; tone?: 'neutral' | 'up' | 'down' | 'warn' }) {
  const cls = { neutral: 'text-ink-0', up: 'text-up', down: 'text-down', warn: 'text-warn' }[tone];
  return (
    <div>
      <div className="text-xs text-ink-2">{label}</div>
      <div className={`mt-1 text-xl font-bold num ${cls}`}>{value}</div>
    </div>
  );
}
